import { Injectable } from '@angular/core';
import { FCanvasComponent } from '@foblex/flow';
import { random } from 'lodash';
import {
  FlowAction,
  FlowConnection,
  FlowNode,
  FlowNodePort,
  FlowNodeTypes,
  FlowState,
  FlowSticker,
  FlowTrigger,
  LINE_WIDTH,
  NODE_WIDTH,
  NodeEditDialogOutput,
  PortRef,
  Position,
  PreviousNode,
  SPACE_BETWEEN_CONDITIONS,
  UiNodeData,
  WorkflowConnectionExport,
  WorkflowExport,
} from '../shared/flow-chart/flow-chart.model';
import { generateGuid } from '@foblex/utils';
import { WorkflowModel } from '@models/workflow.model';
import { AiNodeData, AlertTriggerNodeData, ConditionNodeData, CropNodeData, EchoNodeData, GetImageNodeData, HttpRequestNodeData, JsCodeNodeData, ManualTriggerNodeData, MobilePushNodeData, ResizeNodeData, ScheduleTriggerNodeData, SendEmailNodeData, SendSmsNodeData, SetFpsNodeData, TrimNodeData, VideoNodeData, WaitAlertNodeData, WaitEmailNodeData, WaitMobilePushNodeData, WaitNodeData, WaitSmsNodeData, WaitWebhookNodeData, WebhookNodeData } from '../pages/workflow/node-edit.type';
import { Dictionary } from '@ngrx/entity/src';
import { WorkflowWebhookModel } from '@models/workflow-webhook.model';
import { alertsV2HttpMethodStr } from '@consts/alerts-v2.const';
import { HttpRequestModel } from '@models/http-request.model';

@Injectable({ providedIn: 'root' })
export class FlowNodeService {
  public getViewportCenter(canvas: FCanvasComponent | null): Position {
    canvas.resetScale();
    canvas.redraw();

    const rect = canvas.hostElement.getBoundingClientRect();
    const transform = canvas.transform;

    return {
      x: rect.width / 2 - transform.position.x,
      y: rect.height / 2 - transform.position.y,
    };
  }

  public findNonOverlappingPosition(
    base: Position,
    nodes: (FlowNode | FlowSticker)[],
  ): Position {
    const spacing = NODE_WIDTH + LINE_WIDTH;
    const padding = 20;

    const isFree = (pos: Position): boolean => {
      return !nodes.some((n) =>
        this.positionsOverlap(pos, n.position, NODE_WIDTH + padding),
      );
    };

    if (isFree(base)) {
      return base;
    }

    const directions: Position[] = [
      { x: 1, y: 0 },
      { x: -1, y: 0 },
      { x: 0, y: 1 },
      { x: 0, y: -1 },
      { x: 1, y: 1 },
      { x: 1, y: -1 },
      { x: -1, y: 1 },
      { x: -1, y: -1 },
    ];

    for (let radius = 1; radius <= 8; radius++) {
      for (const dir of directions) {
        const candidate: Position = {
          x: base.x + dir.x * radius * spacing,
          y: base.y + dir.y * radius * spacing,
        };
        if (isFree(candidate)) {
          return candidate;
        }
      }
    }

    return base;
  }

  public createTriggerNode(trigger: FlowTrigger, position: Position): FlowNode {
    const inputs: FlowNodePort[] =
      trigger.inputNames && trigger.inputNames.length > 0
        ? trigger.inputNames.map((name) => ({ id: generateGuid(), name }))
        : [{ id: generateGuid() }];

    const outputs: FlowNodePort[] =
      trigger.outputNames && trigger.outputNames.length > 0
        ? trigger.outputNames.map((name) => ({ id: generateGuid(), name }))
        : [{ id: generateGuid() }];

    return {
      id: generateGuid(),
      name: trigger.name,
      type: trigger.type,
      icon: trigger.icon,
      nodeType: FlowNodeTypes.Trigger,
      position,
      inputs,
      outputs,
      data: null,
      inputData: trigger.inputData,
    };
  }

  public remapClipboardToViewport(
    clip: FlowState,
    targetCenter: Position,
  ): { nodes: FlowNode[]; stickers: FlowSticker[]; connections: FlowConnection[] } {
    const allFlowNodes: (FlowNode | FlowSticker)[] = [...clip.flowNodes.nodes, ...clip.flowNodes.stickers];

    const clipCenter = this.getNodesCenter(allFlowNodes);
    const jitter = random(-2, 2, false) * 10;
    const offsetX = targetCenter.x - clipCenter.x + jitter;
    const offsetY = targetCenter.y - clipCenter.y + jitter;

    const idMap = new Map<string, string>();
    for (const node of allFlowNodes) {
      idMap.set(node.id, generateGuid());
    }

    const portMap = new Map<string, string>();
    for (const node of clip.flowNodes.nodes) {
      for (const input of node.inputs) {
        portMap.set(input.id, generateGuid());
      }
      for (const output of node.outputs) {
        portMap.set(output.id, generateGuid());
      }
    }

    const newNodes: FlowNode[] = clip.flowNodes.nodes.map((n) => {
      return {
        ...n,
        id: idMap.get(n.id)!,
        inputs: n.inputs.map((input) => ({
          ...input,
          id: portMap.get(input.id)!,
        })),
        outputs: n.outputs.map((output) => ({
          ...output,
          id: portMap.get(output.id)!,
        })),
        position: {
          x: n.position.x + offsetX,
          y: n.position.y + offsetY,
        },
      }
    })

    const newStickers: FlowSticker[] = clip.flowNodes.stickers.map((n) => {
      return {
        ...n,
        id: idMap.get(n.id)!,
        position: {
          x: n.position.x + offsetX,
          y: n.position.y + offsetY,
        },
      }
    })

    const newConnections: FlowConnection[] = (clip.connections ?? [])
      .filter((c) => portMap.has(c.from) && portMap.has(c.to))
      .map((c) => ({
        id: generateGuid(),
        from: portMap.get(c.from)!,
        to: portMap.get(c.to)!,
      }));

    return { nodes: newNodes, stickers: newStickers, connections: newConnections };
  }

  public createOutputPorts(outputNames: string[]): FlowNodePort[] {
    return outputNames.map((name) => ({
      id: generateGuid(),
      name,
    }));
  }

  public createInputPorts(inputNames: string[]): FlowNodePort[] {
    return inputNames.map((name) => ({
      id: generateGuid(),
      name,
    }));
  }

  public getNodesCenter(nodes: (FlowNode | FlowSticker)[]): Position {
    if (!nodes.length) {
      return { x: 0, y: 0 };
    }

    const xs = nodes.map((n) => n.position.x);
    const ys = nodes.map((n) => n.position.y);

    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    return {
      x: (minX + maxX) / 2,
      y: (minY + maxY) / 2,
    };
  }

  public calculateConnectedNodePosition(
    sourcePosition: Position,
    outputIndex: number | undefined,
    outputsCount: number,
  ): Position {
    let yPosition = sourcePosition.y;

    if (outputsCount > 1 && outputIndex !== undefined) {
      switch (outputIndex) {
        case 0:
          yPosition -= SPACE_BETWEEN_CONDITIONS;
          break;
        case 1:
          yPosition += SPACE_BETWEEN_CONDITIONS;
          break;
      }
    }

    return {
      x: sourcePosition.x + LINE_WIDTH + NODE_WIDTH,
      y: yPosition,
    };
  }

  public createConnection(
    fromPortId: string,
    toPortId: string,
  ): FlowConnection {
    return {
      id: generateGuid(),
      from: fromPortId,
      to: toPortId,
    };
  }

  public createActionNode(action: FlowAction, position: Position): FlowNode {
    const inputs: FlowNodePort[] =
      action.inputNames && action.inputNames.length > 0
        ? this.createInputPorts(action.inputNames)
        : [{ id: generateGuid() }];

    const outputs: FlowNodePort[] =
      action.outputNames && action.outputNames.length > 0
        ? this.createOutputPorts(action.outputNames)
        : [{ id: generateGuid() }];

    return {
      id: generateGuid(),
      name: action.name,
      type: action.type,
      icon: action.icon,
      nodeType: FlowNodeTypes.Action,
      inputs,
      outputs,
      position,
      data: null,
    };
  }

  public removeConnectionsForNode(
    nodeId: string,
    nodes: FlowNode[],
    connections: FlowConnection[],
  ): FlowConnection[] {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) {
      return connections;
    }

    const inputIds = new Set(node.inputs.map((input) => input.id));
    const outputIds = new Set(node.outputs.map((output) => output.id));

    return connections.filter(
      (connection) =>
        !outputIds.has(connection.from) && !inputIds.has(connection.to),
    );
  }

  public getPreviousNodes(
    node: FlowNode,
    nodes: FlowNode[],
    connections: FlowConnection[],
  ): PreviousNode[] {
    const inputIds = new Set(node.inputs.map((input) => input.id));
    const incomingConnections = connections.filter((c) => inputIds.has(c.to));

    if (!incomingConnections.length) {
      return [];
    }

    const outputIdsFromConnections = new Set(
      incomingConnections.map((c) => c.from),
    );
    return nodes
      .filter(
        (n) =>
          n.outputs.some((output) => outputIdsFromConnections.has(output.id)),
      )
      .map((node) => ({
        id: node.id,
        type: node.type,
        icon: node.icon,
        name: node.name,
      }));
  }

  private positionsOverlap(
    a: Position,
    b: Position,
    threshold: number,
  ): boolean {
    return Math.abs(a.x - b.x) < threshold && Math.abs(a.y - b.y) < threshold;
  }

  public toWorkflowJson(
    flowNodes: {
      nodes: FlowNode[];
      stickers: FlowSticker[];
    },
    connections: FlowConnection[],
    name: string,
    tags?: string[],
  ): WorkflowExport {
    return {
      flowNodes: flowNodes,
      connections: connections,
      name: name,
      tags: tags
    }
  }

  public buildUiNodeData(flowNodes: FlowNode[]): Record<string, UiNodeData> {
    const uiNodeData: Record<string, UiNodeData> = {};
    for (const node of flowNodes) {
      uiNodeData[node.id] = {
        position: node.position,
        inputs: node.inputs,
        outputs: node.outputs,
        nodeType: node.nodeType,
      };
    }

    return uiNodeData;
  }

  public buildBEConnections(
    connections: FlowConnection[],
    flowNodes: FlowNode[],
  ): WorkflowConnectionExport[] {
    const portToNodeMap = new Map<string, PortRef>();

    for (const node of flowNodes) {
      node.outputs.forEach((p, i) =>
        portToNodeMap.set(p.id, { node, portIndex: i, direction: 'output' }),
      );
      node.inputs.forEach((p, i) =>
        portToNodeMap.set(p.id, { node, portIndex: i, direction: 'input' }),
      );
    }

    const workflowConnections: WorkflowConnectionExport[] = connections.map(
      (c) => {
        const from = portToNodeMap.get(c.from)!;
        const to = portToNodeMap.get(c.to)!;

        if (from.direction !== 'output' || to.direction !== 'input') {
          throw new Error('Invalid connection direction');
        }

        return {
          kind: 'main',
          type: to.node.type!,
          fromNode: from.node.id,
          fromPort: from.portIndex,
          toNode: to.node.id,
          toPort: to.portIndex,
        };
      },
    );

    return workflowConnections;
  }

  public buildBEConnectionsAndAssignNodeIds(
    connections: FlowConnection[],
    flowNodes: FlowNode[],
    graph: WorkflowModel.GetGraphResponse,
  ): WorkflowConnectionExport[] {
    const workflowConnections: WorkflowConnectionExport[] =
      this.buildBEConnections(connections, flowNodes);

    for (const connection of workflowConnections) {
      const fromNodeBeId = graph.preview.nodes.find(
        (node) => node.uiId === connection.fromNode,
      )?.nodeId;

      connection.fromNode = fromNodeBeId;

      const toNode = graph.preview.nodes.find(
        (node) => node.uiId === connection.toNode,
      )?.nodeId;

      connection.toNode = toNode;
    }

    return workflowConnections;
  }

  public fromWorkflowJson(json: WorkflowExport): {
    flowNodes: {
      nodes: FlowNode[];
      stickers: FlowSticker[];
    };
    connections: FlowConnection[];
    name: string,
    tags?: string[],
  } {
    return {
      flowNodes: json.flowNodes,
      connections: json.connections,
      name: json.name,
      tags: json.tags,
    }
  }

  public buildNodeUpdateDto(node: FlowNode, data: any): WorkflowModel.UpdateNodeOptionsRequestDto {
    switch (node.type) {
      case WorkflowModel.WorkflowNodeTypes.Webhook:
        return {
          name: (data as WebhookNodeData).name,
          options: {
            webhookId: (data as WebhookNodeData).webhookId,
          },
        };

      case WorkflowModel.WorkflowNodeTypes.Condition:
        return {
          name: (data as ConditionNodeData).name,
          options: data as ConditionNodeData,
        };

      case WorkflowModel.WorkflowNodeTypes.Echo:
        return {
          name: (data as EchoNodeData).name,
          options: {},
        };

      case WorkflowModel.WorkflowNodeTypes.WaitWebhook:
        return {
          name: (data as WaitWebhookNodeData).name,
          options: {
            timeoutMs: ((data as WaitWebhookNodeData).timeoutS ?? 0) * 1000,
            webhookId: (data as WaitWebhookNodeData).webhookId,
          },
        };

      case WorkflowModel.WorkflowNodeTypes.HttpRequest:
        return {
          name: (data as HttpRequestNodeData).name,
          options: {
            httpRequestId: (data as HttpRequestNodeData).httpRequestId,
          },
        };

      case WorkflowModel.WorkflowNodeTypes.AlertTrigger:
        return {
          name: (data as AlertTriggerNodeData).name,
          options: {
            eventId: (data as AlertTriggerNodeData).eventId,
          },
        };

      case WorkflowModel.WorkflowNodeTypes.WaitAlert:
        return {
          name: (data as WaitAlertNodeData).name,
          options: {
            timeoutMs: ((data as WaitAlertNodeData).timeoutS ?? 0) * 1000,
            eventId: (data as WaitAlertNodeData).eventId,
            futureWindowMs: (data as WaitAlertNodeData).futureWindowMs,
            historyWindowMs: (data as WaitAlertNodeData).historyWindowMs,
          },
        };

      case WorkflowModel.WorkflowNodeTypes.SendSms:
        return {
          name: (data as SendSmsNodeData).name,
          options: {
            orgUserIds: (data as SendSmsNodeData).orgUsersIds,
            contactIds: (data as SendSmsNodeData).contactsIds,
            message: (data as SendSmsNodeData).message,
          },
        };

      case WorkflowModel.WorkflowNodeTypes.SendEmail:
        return {
          name: (data as SendEmailNodeData).name,
          options: {
            orgUserIds: (data as SendEmailNodeData).orgUsersIds,
            contactIds: (data as SendEmailNodeData).contactsIds,
            subject: (data as SendEmailNodeData).subject,
            body: (data as SendEmailNodeData).body,
          },
        };

      case WorkflowModel.WorkflowNodeTypes.WaitSms:
        return {
          name: (data as WaitSmsNodeData).name,
          options: {
            timeoutMs: ((data as WaitSmsNodeData).timeoutS ?? 0) * 1000,
          },
        };

      case WorkflowModel.WorkflowNodeTypes.WaitEmail:
        return {
          name: (data as WaitEmailNodeData).name,
          options: {
            timeoutMs: ((data as WaitEmailNodeData).timeoutS ?? 0) * 1000,
          },
        };

      case WorkflowModel.WorkflowNodeTypes.Wait:
        return {
          name: (data as WaitNodeData).name,
          options: {
            seconds: (data as WaitNodeData).seconds,
          },
        };

      case WorkflowModel.WorkflowNodeTypes.MobilePush:
        return {
          name: (data as MobilePushNodeData).name,
          options: {
            orgUserIds: (data as MobilePushNodeData).orgUserIds,
            body: (data as MobilePushNodeData).body,
            title: (data as MobilePushNodeData).title,
          },
        };

      case WorkflowModel.WorkflowNodeTypes.WaitMobilePush:
        return {
          name: (data as WaitMobilePushNodeData).name,
          options: {
            timeoutMs: ((data as WaitMobilePushNodeData).timeoutS ?? 0) * 1000,
          },
        };

      case WorkflowModel.WorkflowNodeTypes.Video:
        return {
          name: (data as VideoNodeData).name,
          options: {
            date: (data as VideoNodeData).date,
            duration: (data as VideoNodeData).duration,
          },
        };

      case WorkflowModel.WorkflowNodeTypes.GetImage:
        return {
          name: (data as GetImageNodeData).name,
          options: {
            date: (data as GetImageNodeData).date,
          },
        };

      case WorkflowModel.WorkflowNodeTypes.SetFps:
        return {
          name: (data as SetFpsNodeData).name,
          options: {
            fps: (data as SetFpsNodeData).fps,
          },
        };

      case WorkflowModel.WorkflowNodeTypes.Trim:
        return {
          name: (data as TrimNodeData).name,
          options: {
            start: (data as TrimNodeData).start,
            end: (data as TrimNodeData).end,
            pad: (data as TrimNodeData).pad,
          },
        };

      case WorkflowModel.WorkflowNodeTypes.Crop:
        return {
          name: (data as CropNodeData).name,
          options: {
            scale: (data as CropNodeData).scale,
          },
        };

      case WorkflowModel.WorkflowNodeTypes.Resize:
        return {
          name: (data as ResizeNodeData).name,
          options: {
            factor: (data as ResizeNodeData).factor,
            maxW: (data as ResizeNodeData).maxW,
          },
        };

      case WorkflowModel.WorkflowNodeTypes.AI:
        return {
          name: (data as AiNodeData).name,
          options: {
            model: (data as AiNodeData).model,
            promt: (data as AiNodeData).promt,
          },
        };

      case WorkflowModel.WorkflowNodeTypes.ManualTrigger:
        return {
          name: (data as ManualTriggerNodeData).name,
          options: {},
        };

      case WorkflowModel.WorkflowNodeTypes.ScheduleTrigger: {
        const schedule = data as ScheduleTriggerNodeData;

        const base = {
          name: schedule.name,
          options: {
            interval: schedule.interval,
          } as Record<string, any>,
        };

        switch (schedule.interval) {
          case WorkflowModel.ScheduleIntervals.Seconds:
            base.options['seconds'] = schedule.seconds;
            break;

          case WorkflowModel.ScheduleIntervals.Minutes:
            base.options['minutes'] = schedule.minutes;
            break;

          case WorkflowModel.ScheduleIntervals.Hours:
            base.options['hours'] = schedule.hours;
            base.options['atMinute'] = schedule.atMinute;
            break;

          case WorkflowModel.ScheduleIntervals.Days:
            base.options['days'] = schedule.days;
            base.options['atHour'] = schedule.atHour;
            base.options['atMinute'] = schedule.atMinute;
            break;

          case WorkflowModel.ScheduleIntervals.Weeks:
            base.options['weeks'] = schedule.weeks;
            base.options['atDay'] = schedule.atDay;
            base.options['atHour'] = schedule.atHour;
            base.options['atMinute'] = schedule.atMinute;
            break;
        }

        return base;
      }

      case WorkflowModel.WorkflowNodeTypes.JsCode:
        return {
          name: (data as JsCodeNodeData).name,
          options: {
            code: (data as JsCodeNodeData).code,
          },
        };

      default:
        return {
          name: '',
          options: {}
        };
    }
  }

  public buildNodeData(
    {
      node,
      dialogData,
      webhooks,
      httpRequests
    }: {
      node: FlowNode,
      dialogData: any,
      webhooks: Dictionary<WorkflowWebhookModel.WebhookEndpointMongoDocument>,
      httpRequests: HttpRequestModel.DocumentMongo[]
    }): {
      url: string;
      method: string;
    } | {
      [x: string]: unknown;
    } {
    const newNodeData = { ...(dialogData as NodeEditDialogOutput).data };

    switch (node.type) {
      case WorkflowModel.WorkflowNodeTypes.Webhook:
        const webhookNodeData = dialogData.data as WebhookNodeData;

        if (webhooks) {
          const url = webhooks[webhookNodeData.webhookId]?.url;
          const method = webhooks[webhookNodeData.webhookId]?.config.method;

          return { ...newNodeData, url: url, method: method };
        }
        break;

      case WorkflowModel.WorkflowNodeTypes.WaitWebhook:
        const waitWebhookNodeData = dialogData.data as WaitWebhookNodeData;

        if (webhooks) {
          const url = webhooks[waitWebhookNodeData.webhookId]?.url;
          const method = webhooks[waitWebhookNodeData.webhookId]?.config.method;

          return { ...newNodeData, url: url, method: method };
        }
        break;

      case WorkflowModel.WorkflowNodeTypes.HttpRequest:
        const httpRequestNodeData = dialogData.data as HttpRequestNodeData;

        if (httpRequests) {
          const httpRequest = httpRequests
            .find((httpRequest) => httpRequest._id === httpRequestNodeData.httpRequestId);

          const url = httpRequest?.address;
          const method = alertsV2HttpMethodStr[httpRequest?.method] as string;

          return { ...newNodeData, url: url, method: method };
        }
        break;
    }

    return { ...newNodeData }
  }
}
